import React, { useState } from 'react';
import { Form, Input, message } from 'antd';
import { LearningStatusSelect, LocaleText, Modal, Table, TestStatusSelect, LectureStatusSelect } from 'ui';
import coursesApiClient from 'utils/coursesApiClient';
import useHandleError from 'utils/useHandleError';
import Spinner from '../../../Spinner';
import { useParams } from 'react-router-dom';
import { useT } from 'hooks/Locale/useT';
import './styles.scss';

interface ActivityStatusModalInterface {
    open: boolean;
    locale?: string;
    onCancel: React.Dispatch<React.SetStateAction<boolean>>;
    students: number[];
    reloadStudents: React.Dispatch<React.SetStateAction<boolean>>;
}

const ActivityStatusModal: React.FC<ActivityStatusModalInterface> = ({
    reloadStudents,
    open,
    onCancel,
    students,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [reload, setReload] = useState(false);
    const [showComment, setShowComment] = useState<any>([]);
    const params = useParams();
    const programId = params.id;
    const [handleError] = useHandleError();
    const [form] = Form.useForm();
    const { TextArea } = Input;
    const t = useT();

    const submit = async (values: any) => {
        setIsLoading(true);
        const parsedValues = {
            enrollmentsId: students,
            activities: values,
            programId: programId,
        };

        try {
            const response = await coursesApiClient.request(
                '/api/v1/courses/learning-program/materials/update-activity-status',
                parsedValues,
                'POST'
            );
            const skipped = response?.data?.skipped ?? [];
            const updated = response?.data?.updated ?? [];

            const summary = t('program.activity_status_change_summary', {
                updated: updated.length,
                skipped: skipped.length,
            });

            const reasons = Array.from(new Set(skipped.map((entry: any) => entry.reason)))
                .map((reason: any) => t(reason))
                .join('; ');

            if (updated.length === 0) {
                // Nothing was written: report it as a failure and keep the modal open so the
                // admin sees why instead of reading a green message as success (CSV-1927).
                message.error(summary);

                if (reasons) {
                    message.warning(reasons);
                }

                reloadStudents(true);

                return;
            }

            message.success(summary);

            if (reasons) {
                message.warning(reasons);
            }

            reloadStudents(true);
            onCancel(false);
            setShowComment([]);
            form.resetFields();
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        form.resetFields();
        setShowComment([]);
        onCancel(false);
    };

    const handleChange = (fieldName: any) => {
        const name = fieldName.join('_');
        setShowComment((showComment: any) => [...showComment, name]);
    };

    const columns = [
        {
            title: t('general.activity'),
            render: (_text: string, record: any) => (
                 record.has_lecture ? record.lecture_title : <LocaleText text={ record.title} />
            ),
        },
        {
            title: t('program.change_activity_status'),
            render: (_text: string, record: any) => {
                const name = [record.id, 'learn'].join('_');

                return (
                    <div className={'full-width-select'}>
                        <LearningStatusSelect
                            type={record.learning_type}
                            onChange={handleChange}
                            disabled={!record.has_learning}
                            name={[record.id, 'learn']}
                        />
                        {showComment.includes(name) && (
                            <Form.Item
                                required
                                name={[record.id, 'learn_comment']}
                                label={t('program.activity_status_change_comment')}
                            >
                                <TextArea maxLength={255} />
                            </Form.Item>
                        )}
                    </div>
                );
            },
        },
        {
            title: t('program.change_test_status'),
            render: (_text: string, record: any) => {
                const testName = [record.id, 'test'].join('_');
                const lectureName = [record.id, 'lecture'].join('_');

                return (
                    <div className={'full-width-select'}>
                        {record.has_lecture ? (
                            <LectureStatusSelect
                                type={record.learning_type}
                                onChange={handleChange}
                                disabled={false}
                                name={[record.id, 'lecture']}
                            />
                        ) : null}
                        {record.has_test ? (
                            <TestStatusSelect
                                type={record.learning_type}
                                onChange={handleChange}
                                disabled={false}
                                name={[record.id, 'test']}
                            />
                        ) : null}
                        {(showComment.includes(testName) || showComment.includes(lectureName)) && (
                            <Form.Item
                                required
                                name={[record.id, record.has_lecture ? 'lecture_comment' : 'test_comment']}
                                label={t('program.activity_status_change_comment')}
                            >
                                <TextArea maxLength={255} />
                            </Form.Item>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <Modal
            open={open}
            width={'70%'}
            isLoading={isLoading}
            onCancel={handleClose}
            className="add_remove_activity"
            destroyOnClose={true}
            title={t('program.activity_status_change')}
            okText={t('general.add')}
            cancelText={t('general.cancel')}
            onOk={form.submit}
        >
            <Spinner spinning={isLoading}>
                <Form layout="vertical" form={form} onFinish={submit}>
                    <Table
                        columns={columns}
                        url={`/api/v1/courses/learning-program/materials/${programId}/activities`}
                        reload={reload}
                        setReload={setReload}
                        rowKey={'id'}
                        isLoading={isLoading}
                        rowSelection={false}
                        pagination={false}
                    />
                </Form>
            </Spinner>
        </Modal>
    );
};

export default ActivityStatusModal;
